import React, {
  useMemo,
  useState,
} from 'react';

import { useProductionForecastStore } from '../../hooks/useProductionForecastStore';

import { ETH } from '../economics/ecoShared';

import AdvancedDeclineModels from './AdvancedDeclineModels';

import SegmentedDeclinePanel from './SegmentedDeclinePanel';

import {
  buildForecastCurve,
  cumulativeDeclineProduction,
  effectiveAnnualDecline,
  fitExponentialDecline,
  fitHarmonicDecline,
  fitHyperbolicDecline,
  predictDeclineRate,
  timeToEconomicLimit,
} from './declineCalculations';

import type {
  DeclineDataPoint,
  DeclineFitResult,
} from './declineCalculations';

export default function DeclinePredictionPanel({
  projectId,
}: {
  projectId: number;
}) {
  const project =
    useProductionForecastStore(
      (state) =>
        state.projects[
          String(projectId)
        ],
    );

  const [
    forecastYears,
    setForecastYears,
  ] =
    useState(5);

  const [
    economicLimit,
    setEconomicLimit,
  ] =
    useState(0.1);

  const productionRows =
    project?.productionRows ??
    [];

  const data =
    useMemo<
      DeclineDataPoint[]
    >(() => {
      const valid =
        productionRows
          .filter(
            (row) =>
              row.date !== '' &&
              row.gasRate !== '',
          )
          .map((row) => ({
            date: row.date,

            q:
              Number(
                row.gasRate,
              ),
          }))
          .filter(
            (row) =>
              Number.isFinite(
                row.q,
              ) &&
              row.q > 0,
          )
          .sort(
            (a, b) =>
              new Date(
                a.date,
              ).getTime() -
              new Date(
                b.date,
              ).getTime(),
          );

      if (
        valid.length === 0
      ) {
        return [];
      }

      const firstTime =
        new Date(
          valid[0].date,
        ).getTime();

      return valid
        .map((row) => {
          const current =
            new Date(
              row.date,
            ).getTime();

          const t =
            (
              current -
              firstTime
            ) /
            (
              24 *
              60 *
              60 *
              1000
            );

          return {
            date:
              row.date,

            t,
            q:
              row.q,
          };
        })
        .filter(
          (row) =>
            Number.isFinite(
              row.t,
            ) &&
            row.t >= 0,
        );
    }, [productionRows]);

  const exponential =
    useMemo(
      () =>
        fitExponentialDecline(
          data,
        ),
      [data],
    );

  const hyperbolic =
    useMemo(
      () =>
        fitHyperbolicDecline(
          data,
        ),
      [data],
    );

  const harmonic =
    useMemo(
      () =>
        fitHarmonicDecline(
          data,
        ),
      [data],
    );

  const models =
    useMemo(
      () =>
        [
          exponential,
          hyperbolic,
          harmonic,
        ].filter(
          (
            item,
          ): item is DeclineFitResult =>
            item !== null,
        ),
      [
        exponential,
        hyperbolic,
        harmonic,
      ],
    );

  const numericalBest =
    useMemo(() => {
      if (
        models.length === 0
      ) {
        return null;
      }

      return [
        ...models,
      ].sort(
        (a, b) =>
          a.rmse -
          b.rmse,
      )[0];
    }, [models]);

  const lastHistoryT =
    data.length > 0
      ? data[
          data.length - 1
        ].t
      : 0;

  const forecastEndByYears =
    lastHistoryT +
    Math.max(
      1,
      forecastYears,
    ) *
      365;

  if (!project) {
    return (
      <div
        style={{
          padding: 20,
          color:
            ETH.textMuted,
        }}
      >
        正在初始化递减预测数据...
      </div>
    );
  }

  return (
    <div>
      {/* 标题 */}
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems:
            'flex-start',
          marginBottom: 16,
        }}
      >
        <div>
          <h3
            style={{
              margin: 0,
            }}
          >
            递减预测
          </h3>

          <div
            style={{
              marginTop: 5,
              fontSize: 11,
              color:
                ETH.textMuted,
            }}
          >
            基于生产动态数据开展
            Arps
            指数、双曲及调和递减分析
          </div>
        </div>

        <div
          style={{
            textAlign:
              'right',
          }}
        >
          <div
            style={{
              fontSize: 11,
              color:
                ETH.textMuted,
            }}
          >
            有效生产数据
          </div>

          <div
            style={{
              marginTop: 3,
              fontSize: 20,
              fontWeight: 800,
              fontFamily:
                ETH.fontMono,
            }}
          >
            {data.length}
          </div>
        </div>
      </div>

      {/* 数据来源 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(3, minmax(0, 1fr))',
          gap: 12,
          marginBottom: 14,
        }}
      >
        <StatusCard
          title="生产记录总数"
          value={`${productionRows.length} 条`}
          description="来自数据准备模块"
        />

        <StatusCard
          title="有效递减点"
          value={`${data.length} 条`}
          description="要求日期与日产气量均有效"
        />

        <StatusCard
          title="生产时间跨度"
          value={
            data.length >= 2
              ? `${Math.round(
                  lastHistoryT,
                )} d`
              : '--'
          }
          description="按生产日期自动计算"
        />
      </div>

      {/* 预测设置 */}
      <div
        style={{
          padding: 14,
          marginBottom: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems:
              'flex-end',
            gap: 24,
            flexWrap: 'wrap',
          }}
        >
          <NumberField
            label="预测年限"
            unit="年"
            value={
              forecastYears
            }
            min={1}
            max={50}
            onChange={
              setForecastYears
            }
          />

          <NumberField
            label="最低产量阈值"
            unit="10⁴m³/d"
            value={
              economicLimit
            }
            min={0.001}
            step={0.01}
            onChange={
              setEconomicLimit
            }
          />

          <div
            style={{
              marginLeft: 'auto',
              fontSize: 11,
              color:
                ETH.textMuted,
            }}
          >
            当前预测起点：
            最后一条有效生产记录
          </div>
        </div>
      </div>

      {data.length < 3 ? (
        <div
          style={{
            height: 260,
            display: 'flex',
            alignItems:
              'center',
            justifyContent:
              'center',
            background:
              ETH.canvasBg,
            border: `1px solid ${ETH.headerBorder}`,
            color:
              ETH.textMuted,
            fontSize: 12,
          }}
        >
          至少需要3条带日期和日产气量的生产记录后进行递减拟合
        </div>
      ) : (
        <>
          {/* 模型拟合结果 */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                'repeat(3, minmax(0, 1fr))',
              gap: 12,
            }}
          >
            <ModelCard
              model={
                exponential
              }
              economicLimit={
                economicLimit
              }
              forecastEndByYears={
                forecastEndByYears
              }
            />

            <ModelCard
              model={
                hyperbolic
              }
              economicLimit={
                economicLimit
              }
              forecastEndByYears={
                forecastEndByYears
              }
            />

            <ModelCard
              model={
                harmonic
              }
              economicLimit={
                economicLimit
              }
              forecastEndByYears={
                forecastEndByYears
              }
            />
          </div>

          {/* 拟合质量摘要 */}
          <div
            style={{
              marginTop: 14,
              padding: 14,
              background:
                ETH.canvasBg,
              border: `1px solid ${ETH.headerBorder}`,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              拟合质量摘要
            </div>

            <div
              style={{
                marginTop: 12,
                display: 'grid',
                gridTemplateColumns:
                  'repeat(4, minmax(0, 1fr))',
                gap: 12,
              }}
            >
              <StatusCard
                title="数值最小 RMSE"
                value={
                  numericalBest
                    ? numericalBest.model
                    : '--'
                }
                description="仅表示当前样本上的数值拟合误差最小"
              />

              <StatusCard
                title="RMSE"
                value={
                  numericalBest
                    ? formatNumber(
                        numericalBest.rmse,
                        4,
                      )
                    : '--'
                }
                description="单位与日产气量一致"
              />

              <StatusCard
                title="R²"
                value={
                  numericalBest
                    ? formatNumber(
                        numericalBest.r2,
                        4,
                      )
                    : '--'
                }
                description="接近1表示样本拟合程度较高"
              />

              <StatusCard
                title="双曲指数 b"
                value={
                  hyperbolic
                    ? formatNumber(
                        hyperbolic.b,
                        3,
                      )
                    : '--'
                }
                description="双曲递减拟合参数"
              />
            </div>
          </div>

          {/* 曲线 */}
          <div
            style={{
              marginTop: 14,
              padding: 14,
              background:
                ETH.canvasBg,
              border: `1px solid ${ETH.headerBorder}`,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                生产递减曲线
              </div>

              <div
                style={{
                  marginTop: 4,
                  fontSize: 11,
                  color:
                    ETH.textMuted,
                }}
              >
                显示历史日产气量及三种
                Arps
                模型从拟合期至预测期的变化
              </div>
            </div>

            <div
              style={{
                marginTop: 12,
              }}
            >
              <DeclineChart
                data={data}
                models={
                  models
                }
                forecastEnd={
                  forecastEndByYears
                }
                economicLimit={
                  economicLimit
                }
              />
            </div>
          </div>

<AdvancedDeclineModels
  data={data}
  forecastEnd={
    forecastEndByYears
  }
/>

<SegmentedDeclinePanel
  data={data}
  forecastEnd={
    forecastEndByYears
  }
/>

          {/* 预测汇总表 */}
          <div
            style={{
              marginTop: 14,
              padding: 14,
              background:
                ETH.canvasBg,
              border: `1px solid ${ETH.headerBorder}`,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                marginBottom: 12,
              }}
            >
              递减预测结果汇总
            </div>

            <PredictionTable
              models={
                models
              }
              lastHistoryT={
                lastHistoryT
              }
              forecastEndByYears={
                forecastEndByYears
              }
              economicLimit={
                economicLimit
              }
            />
          </div>

          {/* 说明 */}
          <div
            style={{
              marginTop: 14,
              padding: 14,
              background:
                ETH.canvasBg,
              border: `1px solid ${ETH.headerBorder}`,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              结果说明
            </div>

            <div
              style={{
                marginTop: 8,
                fontSize: 11,
                lineHeight: 1.8,
                color:
                  ETH.textMuted,
              }}
            >
              <div>
                · 指数递减：
                q=qi·exp(-Di·t)。
              </div>

              <div>
                · 双曲递减：
                q=qi/(1+bDi·t)^(1/b)。
              </div>

              <div>
                · 调和递减为双曲递减
                b=1
                的特殊形式。
              </div>

              <div>
                · “数值最小RMSE”
                只反映当前历史数据的拟合误差，不等同于工程上自动确定唯一适用模型。
              </div>

              <div>
                · 当前预测默认从第一条有效生产记录建立时间轴，并向最后历史点之后延伸。
              </div>

              <div>
                · 下一阶段将增加
                SEPD、Duong、分段递减及异常生产段处理。
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ModelCard({
  model,
  economicLimit,
  forecastEndByYears,
}: {
  model:
    DeclineFitResult | null;

  economicLimit: number;

  forecastEndByYears: number;
}) {
  if (!model) {
    return (
      <div
        style={{
          minHeight: 270,
          padding: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          模型等待拟合
        </div>
      </div>
    );
  }

  const effectiveDecline =
    effectiveAnnualDecline(
      model,
    );

  const economicT =
    timeToEconomicLimit(
      model,
      economicLimit,
    );

  const finalT =
    Number.isFinite(
      economicT,
    ) &&
    economicT > 0
      ? Math.min(
          forecastEndByYears,
          economicT,
        )
      : forecastEndByYears;

  const eur =
    cumulativeDeclineProduction(
      model,
      finalT,
    );

  return (
    <div
      style={{
        padding: 14,
        background:
          ETH.canvasBg,
        border: `1px solid ${ETH.headerBorder}`,
      }}
    >
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
        }}
      >
        {model.model}
      </div>

      <div
        style={{
          marginTop: 12,
          display: 'grid',
          gap: 7,
        }}
      >
        <ResultLine label="qi" value={`${formatNumber(model.qi, 3)} ×10⁴m³/d`} />

        <ResultLine
          label="Di"
          value={`${formatNumber(
            model.di,
            6,
          )} 1/d`}
        />

        <ResultLine
          label="年有效递减率"
          value={`${formatNumber(
            effectiveDecline *
              100,
            2,
          )}%`}
        />

        <ResultLine
          label="b"
          value={
            model.model ===
            '指数递减'
              ? '--'
              : formatNumber(
                  model.b,
                  3,
                )
          }
        />

        <ResultLine
          label="R²"
          value={formatNumber(
            model.r2,
            4,
          )}
        />

        <ResultLine
          label="RMSE"
          value={formatNumber(
            model.rmse,
            4,
          )}
        />

        <ResultLine
          label="MAE"
          value={formatNumber(
            model.mae,
            4,
          )}
        />

        <ResultLine label="模型积分总累计产气（拟合曲线）" value={`${formatNumber(eur, 2)} ×10⁴m³`} strong />
      </div>
    </div>
  );
}

function PredictionTable({
  models,
  lastHistoryT,
  forecastEndByYears,
  economicLimit,
}: {
  models:
    DeclineFitResult[];

  lastHistoryT: number;

  forecastEndByYears: number;

  economicLimit: number;
}) {
  return (
    <div
      style={{
        overflowX: 'auto',
      }}
    >
      <table
        style={{
          width: '100%',
          minWidth: 900,
          borderCollapse:
            'collapse',
          fontSize: 11,
        }}
      >
        <thead>
          <tr>
            <th style={thStyle}>
              模型
            </th>

            <th style={thStyle}>
              当前拟合产量
            </th>

            <th style={thStyle}>
              +1年预测
            </th>

            <th style={thStyle}>
              预测终点产量
            </th>

            <th style={thStyle}>
              达到最低产量阈值时间
            </th>

            <th style={thStyle}>
              模型积分总累计产气（拟合曲线）
            </th>

            <th style={thStyle}>
              R²
            </th>

            <th style={thStyle}>
              RMSE
            </th>
          </tr>
        </thead>

        <tbody>
          {models.map(
            (model) => {
              const economicT =
                timeToEconomicLimit(
                  model,
                  economicLimit,
                );

              const finalT =
                Number.isFinite(
                  economicT,
                ) &&
                economicT > 0
                  ? Math.min(
                      forecastEndByYears,
                      economicT,
                    )
                  : forecastEndByYears;

              const currentQ =
                predictDeclineRate(
                  model,
                  lastHistoryT,
                );

              const q1y =
                predictDeclineRate(
                  model,
                  lastHistoryT +
                    365,
                );

              const endQ =
                predictDeclineRate(
                  model,
                  finalT,
                );

              const eur =
                cumulativeDeclineProduction(
                  model,
                  finalT,
                );

              return (
                <tr
                  key={
                    model.model
                  }
                >
                  <td
                    style={
                      tdStyle
                    }
                  >
                    {
                      model.model
                    }
                  </td>

                  <td
                    style={
                      valueTdStyle
                    }
                  >
                    {`${formatNumber(currentQ, 3)} ×10⁴m³/d`}
                  </td>

                  <td
                    style={
                      valueTdStyle
                    }
                  >
                    {`${formatNumber(q1y, 3)} ×10⁴m³/d`}
                  </td>

                  <td
                    style={
                      valueTdStyle
                    }
                  >
                    {`${formatNumber(endQ, 3)} ×10⁴m³/d`}
                  </td>

                  <td
                    style={
                      valueTdStyle
                    }
                  >
                    {Number.isFinite(
                      economicT,
                    ) &&
                    economicT >
                      0
                      ? `${formatNumber(
                          economicT /
                            365,
                          2,
                        )} 年`
                      : '--'}
                  </td>

                  <td
                    style={
                      valueTdStyle
                    }
                  >
                    {`${formatNumber(eur, 2)} ×10⁴m³`}
                  </td>

                  <td
                    style={
                      valueTdStyle
                    }
                  >
                    {formatNumber(
                      model.r2,
                      4,
                    )}
                  </td>

                  <td
                    style={
                      valueTdStyle
                    }
                  >
                    {formatNumber(
                      model.rmse,
                      4,
                    )}
                  </td>
                </tr>
              );
            },
          )}
        </tbody>
      </table>
    </div>
  );
}

function DeclineChart({
  data,
  models,
  forecastEnd,
  economicLimit,
}: {
  data:
    DeclineDataPoint[];

  models:
    DeclineFitResult[];

  forecastEnd: number;

  economicLimit: number;
}) {
  const width = 900;
  const height = 360;

  const left = 62;
  const right = 30;
  const top = 24;
  const bottom = 50;

  const plotWidth =
    width -
    left -
    right;

  const plotHeight =
    height -
    top -
    bottom;

  const maxT =
    Math.max(
      forecastEnd,
      ...data.map(
        (point) =>
          point.t,
      ),
    );

  const modelCurves =
    models.map(
      (model) => {
        const economicT =
          timeToEconomicLimit(
            model,
            economicLimit,
          );

        const endT =
          Number.isFinite(
            economicT,
          ) &&
          economicT > 0
            ? Math.min(
                maxT,
                economicT,
              )
            : maxT;

        return {
          model,

          curve:
            buildForecastCurve(
              model,
              endT,
              Math.max(
                5,
                endT / 150,
              ),
            ),
        };
      },
    );

  const modelMax =
    Math.max(
      0,
      ...models.map(
        (model) =>
          model.qi,
      ),
    );

  const dataMax =
    Math.max(
      0,
      ...data.map(
        (point) =>
          point.q,
      ),
    );

  const maxQ =
    Math.max(
      1,
      modelMax,
      dataMax,
    ) * 1.08;

  const x = (
    t: number,
  ) =>
    left +
    (t / maxT) *
      plotWidth;

  const y = (
    q: number,
  ) =>
    top +
    (
      1 -
      q / maxQ
    ) *
      plotHeight;

  const xTicks =
    Array.from(
      {
        length: 6,
      },
      (_, index) =>
        maxT *
        index /
        5,
    );

  const yTicks =
    Array.from(
      {
        length: 6,
      },
      (_, index) =>
        maxQ *
        index /
        5,
    );

  const makePath = (
    curve: {
      t: number;
      q: number;
    }[],
  ) =>
    curve
      .map(
        (
          point,
          index,
        ) =>
          `${
            index === 0
              ? 'M'
              : 'L'
          } ${x(
            point.t,
          ).toFixed(
            2,
          )} ${y(
            point.q,
          ).toFixed(
            2,
          )}`,
      )
      .join(' ');

  const styles: Record<
    string,
    {
      stroke: string;
      dash?: string;
    }
  > = {
    指数递减: {
      stroke:
        '#b64f35',
    },

    双曲递减: {
      stroke:
        '#3d6f91',
    },

    调和递减: {
      stroke:
        '#687f54',
      dash:
        '7 5',
    },
  };

  return (
    <div
      style={{
        width: '100%',
        overflowX: 'auto',
      }}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{
          width: '100%',
          minWidth: 720,
          height,
          display: 'block',
        }}
      >
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill={ETH.bg}
        />

        {yTicks.map(
          (value) => (
            <g key={value}>
              <line
                x1={left}
                x2={
                  width -
                  right
                }
                y1={y(value)}
                y2={y(value)}
                stroke={
                  ETH.headerBorder
                }
              />

              <text
                x={
                  left - 10
                }
                y={
                  y(value) +
                  4
                }
                textAnchor="end"
                fontSize={10}
                fill={
                  ETH.textMuted
                }
              >
                {value.toFixed(
                  1,
                )}
              </text>
            </g>
          ),
        )}

        {xTicks.map(
          (value) => (
            <g key={value}>
              <line
                x1={x(value)}
                x2={x(value)}
                y1={top}
                y2={
                  height -
                  bottom
                }
                stroke={
                  ETH.headerBorder
                }
              />

              <text
                x={x(value)}
                y={
                  height -
                  bottom +
                  18
                }
                textAnchor="middle"
                fontSize={10}
                fill={
                  ETH.textMuted
                }
              >
                {Math.round(
                  value,
                )}
              </text>
            </g>
          ),
        )}

        <line
          x1={left}
          x2={left}
          y1={top}
          y2={
            height -
            bottom
          }
          stroke={
            ETH.textSec
          }
        />

        <line
          x1={left}
          x2={
            width -
            right
          }
          y1={
            height -
            bottom
          }
          y2={
            height -
            bottom
          }
          stroke={
            ETH.textSec
          }
        />

        {/* 最低产量阈值线 */}
        {economicLimit >
          0 &&
          economicLimit <
            maxQ && (
            <>
              <line
                x1={left}
                x2={
                  width -
                  right
                }
                y1={y(
                  economicLimit,
                )}
                y2={y(
                  economicLimit,
                )}
                stroke="#8a8a8a"
                strokeDasharray="4 4"
              />

              <text
                x={
                  width -
                  right -
                  4
                }
                y={
                  y(
                    economicLimit,
                  ) - 5
                }
                textAnchor="end"
                fontSize={9}
                fill={
                  ETH.textMuted
                }
              >
                最低产量阈值
              </text>
            </>
          )}

        {modelCurves.map(
          ({
            model,
            curve,
          }) => {
            const style =
              styles[
                model.model
              ];

            return (
              <path
                key={
                  model.model
                }
                d={makePath(
                  curve,
                )}
                fill="none"
                stroke={
                  style.stroke
                }
                strokeWidth={2}
                strokeDasharray={
                  style.dash
                }
              />
            );
          },
        )}

        {/* 历史点 */}
        {data.map(
          (
            point,
            index,
          ) => (
            <circle
              key={`${point.date}-${index}`}
              cx={x(
                point.t,
              )}
              cy={y(
                point.q,
              )}
              r={3.8}
              fill={
                ETH.text
              }
            />
          ),
        )}

        {/* 图例 */}
        {models.map(
          (
            model,
            index,
          ) => {
            const style =
              styles[
                model.model
              ];

            const lx =
              left +
              20 +
              index *
                150;

            return (
              <g
                key={
                  model.model
                }
              >
                <line
                  x1={lx}
                  x2={
                    lx + 30
                  }
                  y1={
                    top + 12
                  }
                  y2={
                    top + 12
                  }
                  stroke={
                    style.stroke
                  }
                  strokeWidth={2}
                  strokeDasharray={
                    style.dash
                  }
                />

                <text
                  x={
                    lx + 36
                  }
                  y={
                    top + 16
                  }
                  fontSize={10}
                  fill={
                    ETH.textSec
                  }
                >
                  {
                    model.model
                  }
                </text>
              </g>
            );
          },
        )}

        <circle
          cx={
            left + 490
          }
          cy={top + 12}
          r={3.8}
          fill={ETH.text}
        />

        <text
          x={
            left + 500
          }
          y={top + 16}
          fontSize={10}
          fill={
            ETH.textSec
          }
        >
          历史数据
        </text>

        <text
          x={
            left +
            plotWidth / 2
          }
          y={height - 8}
          textAnchor="middle"
          fontSize={11}
          fill={
            ETH.textSec
          }
        >
          生产时间 t / d
        </text>

        <text
          x={16}
          y={
            top +
            plotHeight / 2
          }
          textAnchor="middle"
          fontSize={11}
          fill={
            ETH.textSec
          }
          transform={`rotate(-90 16 ${
            top +
            plotHeight /
              2
          })`}
        >
          日产气量 q / 10⁴m³/d
        </text>
      </svg>
    </div>
  );
}

function NumberField({
  label,
  unit,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  unit: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange: (
    value: number,
  ) => void;
}) {
  return (
    <label
      style={{
        display: 'grid',
        gap: 5,
      }}
    >
      <span
        style={{
          fontSize: 11,
          color:
            ETH.textSec,
        }}
      >
        {label}
      </span>

      <div
        style={{
          display: 'flex',
          alignItems:
            'center',
          gap: 7,
        }}
      >
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(
            event,
          ) =>
            onChange(
              Number(
                event.target
                  .value,
              ),
            )
          }
          style={inputStyle}
        />

        <span
          style={{
            fontSize: 11,
            color:
              ETH.textMuted,
          }}
        >
          {unit}
        </span>
      </div>
    </label>
  );
}

function StatusCard({
  title,
  value,
  description,
}: {
  title: string;
  value: string;
  description: string;
}) {
  return (
    <div
      style={{
        padding: 14,
        background:
          ETH.canvasBg,
        border: `1px solid ${ETH.headerBorder}`,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color:
            ETH.textMuted,
        }}
      >
        {title}
      </div>

      <div
        style={{
          marginTop: 6,
          fontSize: 18,
          fontWeight: 800,
          fontFamily:
            ETH.fontMono,
        }}
      >
        {value}
      </div>

      <div
        style={{
          marginTop: 4,
          fontSize: 10,
          color:
            ETH.textMuted,
        }}
      >
        {description}
      </div>
    </div>
  );
}

function ResultLine({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent:
          'space-between',
        gap: 12,
        alignItems:
          'center',
        padding: strong
          ? '9px 0'
          : '5px 0',
        borderTop: strong
          ? `1px solid ${ETH.headerBorder}`
          : undefined,
      }}
    >
      <span
        style={{
          fontSize: 11,
          color:
            ETH.textMuted,
        }}
      >
        {label}
      </span>

      <span
        style={{
          textAlign:
            'right',
          fontSize: strong
            ? 15
            : 11,
          fontWeight: strong
            ? 800
            : 600,
          fontFamily:
            ETH.fontMono,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function formatNumber(
  value: number,
  digits = 4,
) {
  if (
    !Number.isFinite(value)
  ) {
    return '--';
  }

  if (
    Math.abs(value) >
      1e6 ||
    (
      Math.abs(value) >
        0 &&
      Math.abs(value) <
        1e-5
    )
  ) {
    return value.toExponential(
      4,
    );
  }

  return value.toFixed(
    digits,
  );
}

const inputStyle:
  React.CSSProperties = {
    width: 130,
    boxSizing:
      'border-box',
    padding: '6px 8px',
    border: `1px solid ${ETH.headerBorder}`,
    background: ETH.bg,
    color: ETH.text,
    outline: 'none',
  };

const thStyle:
  React.CSSProperties = {
    padding: '8px',
    borderBottom: `1px solid ${ETH.headerBorder}`,
    textAlign: 'center',
    color: ETH.textSec,
    whiteSpace:
      'nowrap',
  };

const tdStyle:
  React.CSSProperties = {
    padding: '8px',
    borderBottom: `1px solid ${ETH.headerBorder}`,
  };

const valueTdStyle:
  React.CSSProperties = {
    ...tdStyle,
    textAlign: 'right',
    fontFamily:
      ETH.fontMono,
  };